// Central date helper. ONE place decides how dates display.
//
//   formatDate(value)          -> "23-07-2026"        (short, dd-mm-yyyy)
//   formatDate(value, "long")  -> "23 July 2026"
//   formatDateTime(value)      -> "26-06-2026 1:05 pm"
//   toInputDate(value)         -> "2026-07-23"        (for <input type="date">)
//
// Two kinds of value, handled differently on purpose:
//
//  * DATE-only ("2026-07-23"): a calendar date, like SQL Server's `date`. It has
//    no time and no timezone, so it displays the same on every machine. We format
//    it straight from its parts — never via new Date("2026-07-23"), which would be
//    UTC midnight and could roll back a day in NZ.
//
//  * TIMESTAMP (an instant, e.g. "2026-06-26 13:05:07+10"): a real moment. It is
//    converted to the APP'S timezone for display, so a US Linode server or a
//    Brisbane dev box still shows NZ wall-clock time. The offset in the value
//    makes the instant unambiguous; the server's own timezone is irrelevant.

// The one knob. claimsdesk = NZ. (otclinic would set Australia/Brisbane.)
const APP_TIME_ZONE =
  (import.meta.env && import.meta.env.VITE_APP_TIME_ZONE) || "Pacific/Auckland";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");
const isDateOnly = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

// Parse a timestamp string/Date into an instant (Date), or null.
function parseInstant(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const iso = String(value)
    .trim()
    .replace(" ", "T")
    .replace(/([+-]\d{2})(\d{2})?$/, (_, h, m) => `${h}:${m || "00"}`); // +10 -> +10:00
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

// The wall-clock parts of an instant, in APP_TIME_ZONE.
function zonedParts(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: APP_TIME_ZONE,
    year: "numeric", month: "numeric", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: false,
  })
    .formatToParts(date)
    .reduce((acc, p) => ((acc[p.type] = p.value), acc), {});
  return {
    y: Number(parts.year),
    mo: Number(parts.month),
    d: Number(parts.day),
    h: Number(parts.hour) % 24, // some locales render midnight as 24
    mi: Number(parts.minute),
  };
}

// Get {y, mo, d} for either a date-only or a timestamp value.
function dateParts(value) {
  if (!value) return null;
  // Date objects go straight to parseInstant — String(date) first would
  // produce "Sun Jul 19 2026 …", which the space→T normaliser mangles into
  // an Invalid Date (the blank-quote-header bug, review 2026-07-19).
  if (value instanceof Date) {
    const inst = parseInstant(value);
    return inst ? zonedParts(inst) : null;
  }
  const s = String(value);
  if (isDateOnly(s)) {
    const [y, mo, d] = s.split("-").map(Number);
    return { y, mo, d };
  }
  const inst = parseInstant(s);
  return inst ? zonedParts(inst) : null;
}

export function formatDate(value, format = "short") {
  const p = dateParts(value);
  if (!p) return "";
  if (format === "long") return `${p.d} ${MONTHS[p.mo - 1]} ${p.y}`;
  return `${pad(p.d)}-${pad(p.mo)}-${p.y}`;
}

export function formatDateTime(value) {
  const inst = parseInstant(value);
  if (!inst) return "";
  const p = zonedParts(inst);
  let h = p.h;
  const ampm = h < 12 ? "am" : "pm";
  h = h % 12 || 12;
  return `${pad(p.d)}-${pad(p.mo)}-${p.y} ${h}:${pad(p.mi)} ${ampm}`;
}

export function toInputDate(value) {
  if (!value) return "";
  if (!(value instanceof Date) && isDateOnly(String(value))) return String(value);
  const p = dateParts(value); // dateParts handles Date objects + strings
  return p ? `${p.y}-${pad(p.mo)}-${pad(p.d)}` : "";
}

/** Shift a date-only YYYY-MM-DD without allowing the machine timezone to move it. */
export function addInputDays(value, days) {
  if (!isDateOnly(String(value || ""))) return "";
  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + Number(days || 0)));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

// ---- "today" / "this month" — the ONLY sanctioned sources. ----
// new Date().toISOString() is UTC: yesterday for the whole NZ morning, so a
// report defaulting to it silently drops today's rows until ~noon. These are
// zone-aware. (Backend twin: services/dates.js localToday/localMonthStart.)

/** Today as "YYYY-MM-DD" in the app timezone (for <input type="date">, filters, comparisons). */
export const todayInput = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: APP_TIME_ZONE });

/** Whole days from `value` to `today`, counted as CALENDAR days in the app zone.
 *  Both ends are reduced to a local YYYY-MM-DD first, so a timestamp late on a
 *  Monday NZ evening and one early on the Tuesday are one day apart rather than
 *  a fraction — which is how a person reads "how long has this sat here". The
 *  arithmetic is done on Date.UTC values built from those parts, the one form
 *  CLAUDE.md permits. Null for an unparseable value. */
export function daysSince(value, today = todayInput()) {
  const from = toInputDate(value);
  if (!from || !isDateOnly(from) || !isDateOnly(String(today))) return null;
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = String(today).split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000);
}

/** How long a job has sat where it is, for the claim rail. Short by design —
 *  the rail is 158px wide. A future timestamp (clock skew on an imported row)
 *  reads as "today" rather than as a negative number. */
export function statusAge(value, today = todayInput()) {
  const n = daysSince(value, today);
  if (n == null) return "";
  if (n <= 0) return "today";
  return n === 1 ? "1 day" : `${n} days`;
}

/** First of (current month + offset) as "YYYY-MM-DD"; offset may be negative.
 *  Pure month arithmetic — immune to the setMonth() end-of-month overflow. */
export function monthStart(offsetMonths = 0) {
  const [y, m] = todayInput().split("-").map(Number);
  const total = y * 12 + (m - 1) + offsetMonths;
  return `${Math.floor(total / 12)}-${pad((((total % 12) + 12) % 12) + 1)}-01`;
}

/** "YYYY-MM" of (current month + offset) in the app timezone. */
export const monthKey = (offsetMonths = 0) => monthStart(offsetMonths).slice(0, 7);
